import { useEffect, useMemo, useState } from 'react';
import '../css/ApacheSuperset.css';
import api from '../services/api';

const EMPTY_DATA = {
  contactosIdentificados: 0,
  contactosEvaluados: 0,
  enSeguimiento: 0,
  seguimientoCompleto: 0,
  pendientes: 0,
  casosSecundarios: 0,
  estadosSeguimiento: [],
  contactosPorCaso: [],
  secundariosPorSemana: [],
};

const SupersetContactsView = ({ scope, appliedFilters, catalogOptions }) => {
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const params = useMemo(() => {
    const findByName = (items, name, idField) => {
      if (!name || /^Tod[oa]s$/i.test(name)) return null;
      const item = (items || []).find(
        (option) => String(option.Nombre).toLocaleLowerCase() === String(name).toLocaleLowerCase()
      );
      return item?.[idField] || null;
    };
    const csv = (value) => Array.isArray(value) ? value.join(',') || undefined : value || undefined;
    return {
      idPatologia: scope?.idPatologia,
      idZonaSanitaria: scope?.idZonaSanitaria,
      idAreaProgramatica: scope?.idAreaProgramatica,
      anio: appliedFilters?.anio || undefined,
      semanas: csv(appliedFilters?.semana),
      provincia: appliedFilters?.provincia || undefined,
      departamentos: csv(appliedFilters?.departamento),
      municipios: csv(appliedFilters?.municipio),
      idSector: findByName(catalogOptions?.sectores, appliedFilters?.sector, 'IdSector'),
      idSubsector: findByName(catalogOptions?.subsectores, appliedFilters?.subsector, 'IdSubsector'),
      idInstitucion: findByName(
        catalogOptions?.instituciones,
        appliedFilters?.efector,
        'IdInstitucionSeguimientoTratamiento'
      ),
      idEstadoCaso: findByName(catalogOptions?.estados, appliedFilters?.estadoCaso, 'IdEstado'),
      idEstadoSeguimiento: findByName(
        catalogOptions?.estados,
        appliedFilters?.estadoSeguimiento,
        'IdEstado'
      ),
    };
  }, [scope, appliedFilters, catalogOptions]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/analytics/contacts', { params })
      .then(({ data: response }) => { if (active) setData(response); })
      .catch(() => { if (active) setError('No se pudieron cargar los indicadores de contactos.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [params]);

  const identified = data.contactosIdentificados || 0;
  const percentage = (value) => identified
    ? `${((value * 100) / identified).toFixed(1)}% de identificados`
    : 'Sin contactos identificados';
  const contactKpis = [
    ['Contactos identificados', identified, 'Personas clasificadas como contacto', 'orange'],
    ['Contactos evaluados', data.contactosEvaluados, percentage(data.contactosEvaluados), 'blue'],
    ['En seguimiento', data.enSeguimiento, percentage(data.enSeguimiento), 'cyan'],
    ['Seguimiento completo', data.seguimientoCompleto, percentage(data.seguimientoCompleto), 'green'],
    ['Pendientes de evaluación', data.pendientes, 'Abiertos y sin controles registrados', 'red'],
  ];
  const funnel = [
    [identified, 'Identificados'],
    [data.contactosEvaluados, 'Evaluados'],
    [data.enSeguimiento, 'En seguimiento'],
    [data.seguimientoCompleto, 'Completo'],
  ];
  const rate = identified ? ((data.seguimientoCompleto * 100) / identified).toFixed(1) : '0,0';
  const perIndex = data.contactosPorCaso || [];
  const perIndexMax = Math.max(1, ...perIndex.map((item) => item.casosIndice));
  const states = data.estadosSeguimiento || [];
  const statesMax = Math.max(1, ...states.map((item) => item.contactos));
  const secondary = data.secundariosPorSemana || [];
  const secondaryMax = Math.max(1, ...secondary.map((item) => item.casos));
  const secondaryPoints = secondary.map((item, index) => {
    const x = secondary.length === 1 ? 320 : 45 + (index / (secondary.length - 1)) * 555;
    const y = 105 - (item.casos / secondaryMax) * 70;
    return `${x},${y}`;
  }).join(' ');

  return (
    <section className="situation-view contacts-view">
      <nav className="situation-view__tabs">
        {['Resumen', 'Situación', 'Contactos', 'Territorio', 'Sector/Efector', 'Calidad', 'Patología', 'Descarga'].map((tab) => (
          <button className={tab === 'Contactos' ? 'is-active' : ''} type="button" key={tab}>{tab}</button>
        ))}
      </nav>
      <div className="situation-view__body">
        <h1 className="situation-view__title">Investigación y seguimiento de contactos</h1>
        {error && <p className="situation-view__error" role="alert">{error}</p>}
        <div className={`situation-view__kpis ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
          {contactKpis.map(([label, value, detail, tone]) => (
            <article className={`situation-card situation-kpi situation-kpi--${tone}`} key={label}>
              <strong>{label}</strong><b>{loading ? '…' : value}</b><small>{loading ? 'Consultando VEC' : detail}</small>
            </article>
          ))}
        </div>
        <div className="contacts-view__main-grid">
          <article className="situation-card contacts-funnel">
            <h2>Embudo de investigación de contactos</h2><p>Flujo acumulado registrado</p>
            <div className="contacts-funnel__body">
              {funnel.map(([value, label], index) => (
                <div className={`contacts-funnel__step step-${index + 1}`} key={label}><b>{value || 0}</b><span>{label}</span></div>
              ))}
            </div>
            <strong className="contacts-funnel__rate">Tasa de finalización: {rate}%</strong>
          </article>
          <article className="situation-card contacts-index">
            <h2>Contactos por caso índice</h2><p>Casos índice agrupados por cantidad de contactos</p>
            {perIndex.length ? <div className="contacts-index__bars">
              {perIndex.map((item) => <i
                key={item.cantidadContactos}
                title={`${item.cantidadContactos} contactos: ${item.casosIndice} casos índice`}
                style={{ height: `${Math.max(5, (item.casosIndice * 100) / perIndexMax)}%` }}
              ><span>{item.cantidadContactos}</span></i>)}
            </div> : <p className="real-bars-empty">Sin relaciones caso-contacto para los filtros seleccionados</p>}
            <strong>Casos secundarios detectados: {data.casosSecundarios || 0}</strong>
          </article>
          <article className="situation-card contacts-states">
            <h2>Estados del seguimiento</h2><p>Estado operativo registrado</p>
            {states.length ? states.map((item) => (
              <div key={item.estado}><span>{item.estado}</span><i style={{ width: `${Math.max(4, (item.contactos * 100) / statesMax)}%` }} /><b>{item.contactos}</b></div>
            )) : <p className="real-bars-empty">Sin estados registrados</p>}
          </article>
        </div>
        <div className="contacts-view__lower-grid">
          <article className="situation-card situation-card--trend">
            <h2>Casos secundarios detectados</h2><p>Contactos que también están clasificados como caso</p>
            <svg viewBox="0 0 620 130">
              <line x1="45" x2="600" y1="105" y2="105"/><line x1="45" x2="600" y1="70" y2="70"/><line x1="45" x2="600" y1="35" y2="35"/>
              {secondary.length ? <polyline className="secondary" points={secondaryPoints}/> : <text className="empty-chart" x="310" y="70">Sin casos secundarios</text>}
            </svg>
          </article>
          <article className="situation-card situation-notes">
            <h2>Fuente y criterio operativo</h2>
            <p>Datos reales de VEC</p>
            <ul>
              <li>Evaluado: tiene al menos un control de seguimiento.</li>
              <li>Completo: fecha de fin o estado configurado como cerrado.</li>
              <li>Pendiente: seguimiento abierto sin controles registrados.</li>
              <li>Vencimientos no calculados: falta una fecha esperada de próximo control.</li>
            </ul>
            <small className="situation-source">Último registro: {data.ultimaFechaRegistro ? new Date(data.ultimaFechaRegistro).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : 'sin fecha'} · Consulta: {data.actualizadoEn ? new Date(data.actualizadoEn).toLocaleString('es-AR') : '—'}</small>
          </article>
        </div>
      </div>
    </section>
  );
};

export default SupersetContactsView;
